import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { decodeEventLog, keccak256, toBytes, type Address, type Hash } from "viem";
import { polygon } from "viem/chains";
import {
  PROJECT_REGISTRY_ABI,
  PROJECT_REGISTRY_ADDRESS,
  UNIVERSAL_IDENTITY_ABI,
  UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
} from "./config";

export type LaunchTrustInput = {
  enabled?: boolean;
  entityId?: string;
  entityType: 1 | 2 | 3;
  entityMetadataURI?: string;
  projectMetadataURI?: string;
  projectKind: 1 | 2 | 3;
};

export type LaunchTrustRegistration = {
  entityId: bigint;
  projectId: bigint;
  entityTransaction?: Hash;
  projectTransaction: Hash;
};

const eventId = (
  logs: readonly { address: Address; data: Hash; topics: readonly Hash[] }[],
  address: Address,
  abi: typeof UNIVERSAL_IDENTITY_ABI | typeof PROJECT_REGISTRY_ABI,
  eventName: "EntityRegistered" | "ProjectRegistered",
  field: "entityId" | "projectId",
) => {
  for (const log of logs) {
    if (log.address.toLowerCase() !== address.toLowerCase()) continue;
    try {
      const decoded = decodeEventLog({ abi, data: log.data, topics: log.topics as [Hash, ...Hash[]] });
      if (decoded.eventName === eventName) return (decoded.args as unknown as Record<string, bigint>)[field];
    } catch {
      // Ignore unrelated logs from the transaction.
    }
  }
  throw new Error(`${eventName} was not found in the transaction receipt.`);
};

export const launchTrustConfigured = Boolean(
  UNIVERSAL_IDENTITY_REGISTRY_ADDRESS && PROJECT_REGISTRY_ADDRESS,
);

export function useLaunchTrustRegistration() {
  const { address } = useAccount();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { data: walletClient } = useWalletClient();

  const send = async (contract: Address, abi: readonly unknown[], functionName: string, args: readonly unknown[]) => {
    if (!address || !publicClient || !walletClient) throw new Error("Connect a wallet before trust registration.");
    const simulation = await publicClient.simulateContract({ address: contract, abi, functionName, args, account: address } as never);
    const hash = await walletClient.writeContract(simulation.request);
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") throw new Error(`${functionName} reverted on Polygon.`);
    return { hash, receipt };
  };

  const registerBeforeLaunch = async (input: LaunchTrustInput): Promise<LaunchTrustRegistration | undefined> => {
    if (!input.enabled) return undefined;
    if (!UNIVERSAL_IDENTITY_REGISTRY_ADDRESS || !PROJECT_REGISTRY_ADDRESS) {
      throw new Error("Trust registration contracts are not configured.");
    }
    if (!input.projectMetadataURI?.trim()) throw new Error("A project metadata URI is required for a verified launch.");

    let entityId: bigint;
    let entityTransaction: Hash | undefined;
    if (input.entityId?.trim()) {
      entityId = BigInt(input.entityId);
      if (entityId <= 0n) throw new Error("Founder entity ID must be greater than zero.");
      if (!address || !publicClient) throw new Error("Connect a wallet before trust registration.");
      const controller = await publicClient.readContract({
        address: UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
        abi: UNIVERSAL_IDENTITY_ABI,
        functionName: "controllerOf",
        args: [entityId],
      } as never) as Address;
      if (controller.toLowerCase() !== address.toLowerCase()) throw new Error("The connected wallet does not control that entity.");
    } else {
      if (!input.entityMetadataURI?.trim()) throw new Error("An entity metadata URI is required when creating a new entity.");
      const transaction = await send(
        UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
        UNIVERSAL_IDENTITY_ABI,
        "registerEntity",
        [input.entityType, input.entityMetadataURI, keccak256(toBytes(input.entityMetadataURI))],
      );
      entityTransaction = transaction.hash;
      entityId = eventId(
        transaction.receipt.logs,
        UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
        UNIVERSAL_IDENTITY_ABI,
        "EntityRegistered",
        "entityId",
      );
    }

    const projectTransaction = await send(
      PROJECT_REGISTRY_ADDRESS,
      PROJECT_REGISTRY_ABI,
      "registerProject",
      [entityId, input.projectKind, input.projectMetadataURI, keccak256(toBytes(input.projectMetadataURI))],
    );
    const projectId = eventId(
      projectTransaction.receipt.logs,
      PROJECT_REGISTRY_ADDRESS,
      PROJECT_REGISTRY_ABI,
      "ProjectRegistered",
      "projectId",
    );
    return { entityId, projectId, entityTransaction, projectTransaction: projectTransaction.hash };
  };

  const bindLaunchAsset = async (projectId: bigint, launchAsset: Address) => {
    if (!PROJECT_REGISTRY_ADDRESS) throw new Error("Project registry is not configured.");
    return (await send(PROJECT_REGISTRY_ADDRESS, PROJECT_REGISTRY_ABI, "bindLaunchAsset", [projectId, launchAsset])).hash;
  };

  return { configured: launchTrustConfigured, registerBeforeLaunch, bindLaunchAsset };
}
